/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

import { Auth } from "aws-amplify";
import updateIdpUserAttributes from "./updateIdpUserAttributes";

import {
	storeTokens,
	setTokenCookie,
	setRefreshTokenCookie,
} from "./tokenHelper";
import { eraseCookie } from "./cookieHelper";

const handleAuthUIStateChange = async (authState, siteKey, lang) => {
	if (authState === "authenticated") {
		let redirect_uri;
		let client_id;
		let authorization_code;
		let clientState;
		let clientNonce;
		let clientScope;
		let queryStringParams = new URLSearchParams(window.location.search);
		let qsRedirectUri = queryStringParams.get("redirect_uri");
		let qsClientId = queryStringParams.get("default_client_id");
		let qsAuthorizationCode = queryStringParams.get("authorization_code");
		let qsClientState = queryStringParams.get("state");
		let qsClientNonce = queryStringParams.get("nonce");
		let qsClientScope = queryStringParams.get("scope");

		if (!siteKey) await updateIdpUserAttributes(lang);

		/*
		 * For a local sign in the redirect_uri/authorization_code will be in the query string params
		 */
		if (qsRedirectUri) {
			redirect_uri = qsRedirectUri;
			client_id = qsClientId;
			authorization_code = qsAuthorizationCode;
			clientState = qsClientState;
			clientNonce = qsClientNonce;
			clientScope = qsClientScope;
		} else {
			/*
			 * For a federated sign in the redirect_uri/authorization_code will be in the local storage
			 */
			redirect_uri = localStorage.getItem("client-redirect-uri");
			client_id = localStorage.getItem("client-default-client-id");
			authorization_code = localStorage.getItem("authorization_code");
			clientState = localStorage.getItem("client-state");
			clientNonce = localStorage.getItem("client-nonce");
			clientScope = localStorage.getItem("client-scope");
			localStorage.removeItem(`client-redirect-uri`);
			localStorage.removeItem(`authorization_code`);
			localStorage.removeItem(`client-state`);
			localStorage.removeItem(`client-nonce`);
			localStorage.removeItem(`client-scope`);
		}

		/*
		 * get the current user session
		 */
		let authInfo = await Auth.currentSession();

		let idToken = authInfo.idToken.jwtToken;
		let accessToken = authInfo.accessToken.jwtToken;
		let refreshToken = authInfo.refreshToken.token;

		/*
		 * Set the ID and access token cookies for fast SSO
		 */
		if (idToken && accessToken && refreshToken) {
			setTokenCookie("id_token", idToken);
			setTokenCookie("access_token", accessToken);

			/*
			 * Set the refresh token cookie. Refresh token cannot be parsed for an an expiry so use the access token to get an expiry.
			 * Although the refresh token has a different (longer) expiry than the access token, this is for the purpose of fast SSO,
			 * so the refresh token cookie will get set again when the id or access token cookie expires
			 */
			setRefreshTokenCookie(refreshToken, accessToken);
		} else {
			console.error(
				"Inconsistent application state: Tokens missing from current session"
			);
			return;
		}

		if (authorization_code && redirect_uri) {
			/*
			 * PKCE Flow
			 */

			try {
				//Store tokens in DynamoDB
				const response = await storeTokens(
					authorization_code,
					redirect_uri,
					client_id,
					idToken,
					accessToken,
					refreshToken,
					clientNonce,
					clientScope
				);

				if (response.status === 200) {
					// OIDC
					const redirectUrl =
						redirect_uri +
						"?code=" +
						authorization_code +
						(clientState !== undefined
							? "&state=" + clientState
							: "");
					window.location.replace(redirectUrl);
				} else if (response.status === 201) {
					// BigCommerce
					const { token, url } = response.data;
					const redirectUrl = url + token;
					window.location.replace(redirectUrl);
				} else {
					console.error(
						"Could not store tokens. Server response: " +
							response.data
					);
					return;
				}
			} catch (error) {
				console.error(
					"Could not store tokens. Server response: " + error
				);
				return;
			}
		} else if (redirect_uri) {
			/*
			 * Implicit Flow
			 */
			window.location.replace(
				redirect_uri +
					"?id_token=" +
					idToken +
					(clientState !== undefined ? "&state=" + clientState : "")
			);
		}
	} else if (authState === "signOut") {
		eraseCookie("id_token");
		eraseCookie("access_token");
		eraseCookie("refresh_token");
	}
};

export default handleAuthUIStateChange;
